import type { EfemGatewayLoadPortConfig } from '@cnapse/shared/schemas.js';
import { providerEfemService } from '../../../services/ProviderEfemService';
import { tenantDevicesService } from '../../../services/TenantDevicesService';
import { efemService } from '../../../services/EfemService';
import type { EfemGatewaySetupDraft, HandheldScannerRow, LoadPortRow, WaferCameraEditRow } from './efemGatewaySetup.types';

export async function saveEfemGatewaySetup(params: {
  gatewaySerialNumber: string;
  tenantId?: string;
  draft: EfemGatewaySetupDraft;
  baseline: EfemGatewaySetupDraft;
}): Promise<void> {
  const { gatewaySerialNumber, tenantId, draft, baseline } = params;
  const trimmedName = draft.name.trim();
  if (!trimmedName) {
    throw new Error('Gateway name is required');
  }

  if (trimmedName !== baseline.name.trim()) {
    if (tenantId) {
      await providerEfemService.updateProviderEfemClient(tenantId, gatewaySerialNumber, { name: trimmedName });
    } else {
      await efemService.updateEfemClient(gatewaySerialNumber, { name: trimmedName });
    }
  }

  await persistLoadPorts(gatewaySerialNumber, tenantId, draft.loadPorts, baseline.loadPorts);
  await persistHandheld(gatewaySerialNumber, draft.handheld, baseline.handheld);
  await persistCameras(gatewaySerialNumber, draft.cameras, baseline.cameras);
}

function toLoadPortProfileConfigs(rows: LoadPortRow[]): EfemGatewayLoadPortConfig[] {
  return rows
    .map((r) => ({ id: Number(r.id), size: r.size }))
    .sort((a, b) => a.id - b.id);
}

/** Compare load port id/size list only (scanner bindings are persisted separately). */
export function loadPortProfilesEqual(a: LoadPortRow[], b: LoadPortRow[]): boolean {
  const left = toLoadPortProfileConfigs(a);
  const right = toLoadPortProfileConfigs(b);
  if (left.length !== right.length) return false;
  return left.every((port, i) => port.id === right[i].id && port.size === right[i].size);
}

function scannerBindingKey(rows: LoadPortRow[]): string {
  return rows
    .filter((r) => r.scannerSerial)
    .map((r) => `${r.id}:${r.scannerSerial}`)
    .sort()
    .join('|');
}

export function loadPortScannerBindingsEqual(a: LoadPortRow[], b: LoadPortRow[]): boolean {
  return scannerBindingKey(a) === scannerBindingKey(b);
}

export function findBaselineCameraRow(
  baseline: WaferCameraEditRow[],
  row: WaferCameraEditRow,
): WaferCameraEditRow | undefined {
  return (
    baseline.find((b) => b.roleKey === row.roleKey)
    ?? baseline.find((b) => b.boundSerial && b.boundSerial === row.boundSerial)
  );
}

/** Fields sent on PATCH /efem-peripheral for wafer cameras. */
export function cameraRowPatchEqual(a: WaferCameraEditRow, b: WaferCameraEditRow): boolean {
  return (
    a.boundSerial === b.boundSerial
    && a.displayLabel === b.displayLabel
    && a.supportedWaferSize === b.supportedWaferSize
    && a.idSide === b.idSide
    && a.mountCalibrationDeg === b.mountCalibrationDeg
  );
}

function collectScannersToUnbind(previousRows: LoadPortRow[], nextRows: LoadPortRow[]): string[] {
  const scannersToUnbind = new Set<string>();
  for (const prev of previousRows) {
    if (!prev.scannerSerial) continue;
    const next = nextRows.find((r) => r.id === prev.id);
    if (!next || next.scannerSerial !== prev.scannerSerial) {
      scannersToUnbind.add(prev.scannerSerial);
    }
  }
  for (const removed of previousRows) {
    if (!nextRows.some((r) => r.id === removed.id) && removed.scannerSerial) {
      scannersToUnbind.add(removed.scannerSerial);
    }
  }
  return [...scannersToUnbind];
}

function collectScannersToBind(previousRows: LoadPortRow[], nextRows: LoadPortRow[]): LoadPortRow[] {
  const prevById = new Map(previousRows.map((r) => [r.id, r]));
  return nextRows.filter((row) => {
    if (!row.scannerSerial) return false;
    const prev = prevById.get(row.id);
    return prev?.scannerSerial !== row.scannerSerial;
  });
}

async function persistLoadPorts(
  gatewaySerialNumber: string,
  tenantId: string | undefined,
  nextRows: LoadPortRow[],
  previousRows: LoadPortRow[],
): Promise<void> {
  const profileChanged = !loadPortProfilesEqual(nextRows, previousRows);
  const bindingsChanged = !loadPortScannerBindingsEqual(nextRows, previousRows);
  if (!profileChanged && !bindingsChanged) {
    return;
  }

  if (profileChanged) {
    const loadPorts = toLoadPortProfileConfigs(nextRows);
    if (tenantId) {
      await providerEfemService.putProviderEfemHardwareProfile(tenantId, gatewaySerialNumber, { loadPorts });
    } else {
      await efemService.putEfemHardwareProfile(gatewaySerialNumber, { loadPorts });
    }
  }

  if (!bindingsChanged) {
    return;
  }

  for (const serial of collectScannersToUnbind(previousRows, nextRows)) {
    await tenantDevicesService.patchTenantEfemPeripheral(serial, null);
  }

  for (const row of collectScannersToBind(previousRows, nextRows)) {
    await tenantDevicesService.patchTenantEfemPeripheral(row.scannerSerial, {
      parentGatewaySerialNumber: gatewaySerialNumber,
      loadPortId: Number(row.id),
    });
  }
}

/** Fields sent on PATCH /efem-peripheral for handheld scanners. */
export function handheldRowPatchEqual(a: HandheldScannerRow, b: HandheldScannerRow): boolean {
  return a.boundSerial === b.boundSerial && a.displayLabel === b.displayLabel;
}

async function persistHandheld(
  gatewaySerialNumber: string,
  next: HandheldScannerRow,
  previous: HandheldScannerRow,
): Promise<void> {
  if (handheldRowPatchEqual(next, previous)) {
    return;
  }

  if (!next.boundSerial) {
    if (previous.boundSerial) {
      await tenantDevicesService.patchTenantEfemPeripheral(previous.boundSerial, null);
    }
    return;
  }

  if (previous.boundSerial && previous.boundSerial !== next.boundSerial) {
    await tenantDevicesService.patchTenantEfemPeripheral(previous.boundSerial, null);
  }

  await tenantDevicesService.patchTenantEfemPeripheral(next.boundSerial, {
    parentGatewaySerialNumber: gatewaySerialNumber,
    displayLabel: next.displayLabel || undefined,
  });
}

async function persistCameras(
  gatewaySerialNumber: string,
  nextRows: WaferCameraEditRow[],
  previousRows: WaferCameraEditRow[],
): Promise<void> {
  const previousSerials = new Set(previousRows.map((r) => r.boundSerial).filter(Boolean));
  const nextSerials = new Set(nextRows.map((r) => r.boundSerial).filter(Boolean));

  const serialsToUnbind = [...previousSerials].filter((serial) => !nextSerials.has(serial));
  const rowsToBind = nextRows.filter((row) => {
    if (!row.boundSerial) return false;
    const baselineRow = findBaselineCameraRow(previousRows, row);
    return !baselineRow || !cameraRowPatchEqual(baselineRow, row);
  });

  if (serialsToUnbind.length === 0 && rowsToBind.length === 0) {
    return;
  }

  for (const serial of serialsToUnbind) {
    await tenantDevicesService.patchTenantEfemPeripheral(serial, null);
  }

  for (const row of rowsToBind) {
    await tenantDevicesService.patchTenantEfemPeripheral(row.boundSerial, {
      parentGatewaySerialNumber: gatewaySerialNumber,
      displayLabel: row.displayLabel || undefined,
      supportedWaferSize: row.supportedWaferSize,
      idSide: row.idSide,
      mountCalibrationDeg: row.mountCalibrationDeg,
    });
  }
}

export function draftsEqual(a: EfemGatewaySetupDraft, b: EfemGatewaySetupDraft): boolean {
  if (a.name.trim() !== b.name.trim()) return false;
  if (!loadPortProfilesEqual(a.loadPorts, b.loadPorts)) return false;
  if (!loadPortScannerBindingsEqual(a.loadPorts, b.loadPorts)) return false;
  if (!handheldRowPatchEqual(a.handheld, b.handheld)) return false;
  if (a.cameras.length !== b.cameras.length) return false;
  return a.cameras.every((row) => {
    const baselineRow = findBaselineCameraRow(b.cameras, row);
    return baselineRow != null && cameraRowPatchEqual(baselineRow, row);
  });
}
